import React, { useMemo, useState } from "react"

import { sanitize } from "../../../utils/utils"
import { formatBlogPostDate } from "../../Blog/blogDateUtils"
import BlogIndexHeader from "../../Blog/BlogIndexHeader"
import { collectBlogTags, filterPostsByTag, formatTagLabel } from "../../Blog/blogTagUtils"
import { getSectionColorScheme } from "../utils/colorModeUtils"
import { formatFontFamily, formatHeadingFontFamily } from "../utils/fontUtils"
import { resolveBuilderPageSlug, scopePath } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

import { ScrollRevealItem } from "./shared/ScrollReveal"

const tagPillStyle = (textDark) => ({
  backgroundColor: "#e4eff6",
  color: textDark
})

const normalizePost = (post) => ({
  ...post,
  publishedAt: post.publishedAt ?? post.published_at,
  photoUrl: post.photoUrl ?? post.photo_url,
  tags: post.tags ?? []
})

const BlogPostsSectionDesktop = ({ section, practice, theme, highlightedField, viewingInBuilder = false }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const colorScheme = getSectionColorScheme(settings, theme)
  const headingFont = formatHeadingFontFamily(theme?.fonts?.heading)
  const paragraphFont = formatFontFamily(theme?.fonts?.paragraph)
  const textDark = theme?.colors?.textDark || colorScheme.bodyColor || "#55433f"
  const accentColor = theme?.colors?.primary || theme?.colors?.primaryDark || "#0d9488"
  const headerBackground = theme?.colors?.bgLight || theme?.colors?.background || "#f4f7f9"
  const pageTitle = practice?.blogPageTitle || practice?.blog_page_title || "Blog"

  const posts = useMemo(
    () => (practice?.blogPosts || practice?.blog_posts || []).map(normalizePost),
    [practice?.blogPosts, practice?.blog_posts]
  )

  const allTags = useMemo(() => collectBlogTags(posts), [posts])
  const [activeTag, setActiveTag] = useState(null)
  const filteredPosts = useMemo(() => filterPostsByTag(posts, activeTag), [posts, activeTag])

  const selectTag = (tag) => {
    setActiveTag(tag)
  }

  return (
    <div className={`bg-white ${isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""}`}>
      <BlogIndexHeader
        pageTitle={pageTitle}
        postCount={posts.length}
        filteredCount={filteredPosts.length}
        activeTag={activeTag}
        tags={allTags}
        onSelectTag={selectTag}
        headingFont={headingFont}
        headingWeight={theme?.fonts?.headingWeight}
        paragraphFont={theme?.fonts?.paragraph}
        textDark={textDark}
        accentColor={accentColor}
        headerBackground={headerBackground}
      />

      <main className="mx-auto max-w-3xl px-5 pb-12 pt-4 md:pt-2">
        {filteredPosts.length === 0 ? (
          <p className="text-lg text-gray-dark" style={{ fontFamily: paragraphFont }}>
            {activeTag ? (
              <>
                No posts tagged &ldquo;{formatTagLabel(activeTag)}&rdquo;.{" "}
                <button
                  type="button"
                  className="font-semibold text-teal hover:underline"
                  onClick={() => selectTag(null)}>
                  View all posts
                </button>
              </>
            ) : viewingInBuilder ? (
              <>
                No posts yet. Manage posts in the{" "}
                <a href="/portal/website/blog" className="font-semibold text-teal hover:underline">
                  Blog admin
                </a>
                .
              </>
            ) : (
              "No posts yet."
            )}
          </p>
        ) : (
          <div className="flex flex-col gap-10">
            {filteredPosts.map((post, index) => {
              const publishedLabel = formatBlogPostDate(post.publishedAt)
              const blogPageSlug = resolveBuilderPageSlug(practice, "blog", "blog")
              const postUrl = scopePath(`/${blogPageSlug}/${post.slug}`, practice)

              return (
                <ScrollRevealItem
                  key={post.id}
                  staggerIndex={index + 1}
                  as="article"
                  className="border-b border-gray-light pb-10">
                  <a href={postUrl} className="group block">
                    {post.photoUrl && (
                      <img src={post.photoUrl} alt="" className="mb-4 aspect-[3/2] w-full rounded-xl object-cover" />
                    )}
                    {publishedLabel && (
                      <time
                        dateTime={post.publishedAt}
                        className="mb-2 block text-sm font-medium"
                        style={{ color: textDark, opacity: 0.7, fontFamily: paragraphFont }}>
                        {publishedLabel}
                      </time>
                    )}
                    <h2
                      className="mb-2 text-2xl font-bold group-hover:underline"
                      style={{ color: textDark, fontFamily: headingFont, fontWeight: theme?.fonts?.headingWeight }}>
                      {post.title}
                    </h2>
                    {post.subtitle && (
                      <p className="mb-2 text-lg" style={{ color: textDark, opacity: 0.85, fontFamily: paragraphFont }}>
                        {post.subtitle}
                      </p>
                    )}
                    {post.tags?.length > 0 && (
                      <div className="mb-3 flex flex-wrap gap-2">
                        {post.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full px-2 py-0.5 text-xs font-medium"
                            style={tagPillStyle(textDark)}>
                            {formatTagLabel(tag)}
                          </span>
                        ))}
                      </div>
                    )}
                    {post.content && (
                      <div
                        className="wysiwyg-content line-clamp-3 text-base text-gray-dark"
                        dangerouslySetInnerHTML={{ __html: sanitize(post.content) }}
                      />
                    )}
                  </a>
                </ScrollRevealItem>
              )
            })}
          </div>
        )}
      </main>
    </div>
  )
}

export default BlogPostsSectionDesktop
